import { Link, getRouteApi, useNavigate, useRouter, type LinkProps } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import { KNOWLEDGE_MODES, MODES, ModeNavigator, itemKey, type KnowledgeMode, type ModeColumn, type ModeItem, type ModeWorldData } from '@realmery/realm-core';
import { keyLabel, keysOf, useShortcutKeys } from '../shortcuts.ts';

/**
 * Knowledge Modes (docs/PARITY-STEP-5-PLAN.md section 9), as the desktop's: five ways of
 * walking the same realm, each a chain of columns where picking something fills the next
 * with what's connected to it, a path line of what's picked, and a preview of the last
 * pick with a button to open it in its own page. The columns are worked out here from one
 * snapshot of the realm; ↻ reads it again. Maps, pins and regions open in the Atlas.
 */

const route = getRouteApi('/_app/realm/$realmKey/modes');

export const MODE_SLUGS = ['explorer', 'writer', 'game-master', 'architect', 'research'] as const;
export type ModeSlug = (typeof MODE_SLUGS)[number];
const SLUG_MODES: Record<ModeSlug, KnowledgeMode> = { explorer: 'EXPLORER', writer: 'WRITER', 'game-master': 'GAME_MASTER', architect: 'ARCHITECT', research: 'RESEARCH' };
const MODE_SHORTCUTS: Record<KnowledgeMode, string> = { EXPLORER: 'mode.explorer', WRITER: 'mode.writer', GAME_MASTER: 'mode.gameMaster', ARCHITECT: 'mode.architect', RESEARCH: 'mode.research' };
const slugOf = (mode: KnowledgeMode) => MODE_SLUGS.find((s) => SLUG_MODES[s] === mode)!;

/** The path last walked in each mode, for coming back to it (`REMEMBERED_PATHS`). */
const remembered = new Map<ModeSlug, string>();

/** Rebuilds the path from the picked keys, as far as they still exist (`restore`). */
function walk(navigator: ModeNavigator, mode: KnowledgeMode, keys: readonly string[]): { path: ModeItem[]; columns: ModeColumn[] } {
  const path: ModeItem[] = [];
  const last = KNOWLEDGE_MODES[mode].columns.length - 1;
  for (const key of keys) {
    const match = navigator.column(mode, path.length, path).items.find((i) => itemKey(i) === key);
    if (!match) break;
    path.push(match);
    if (path.length > last) break;
  }
  const shown = Math.min(path.length + 1, KNOWLEDGE_MODES[mode].columns.length);
  const columns = Array.from({ length: shown }, (_, i) => navigator.column(mode, i, path));
  return { path, columns };
}

export function KnowledgeModesPage() {
  const world = route.useLoaderData() as ModeWorldData;
  const search = route.useSearch();
  const { realmKey } = route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  useShortcutKeys();
  const slug: ModeSlug = search.mode ?? 'explorer';
  const mode = SLUG_MODES[slug];
  const navigator = useMemo(() => new ModeNavigator(world), [world]);
  const keys = (search.path ?? '').split('|').filter((k) => k !== '');
  const { path, columns } = walk(navigator, mode, keys);
  const lastPick = path.at(-1) ?? null;
  const columnsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    remembered.set(slug, path.map(itemKey).join('|'));
    // The newest column comes into view.
    columnsRef.current?.scrollTo({ left: columnsRef.current.scrollWidth, behavior: 'smooth' });
  });

  const go = (nextSlug: ModeSlug, picked: readonly ModeItem[]) =>
    navigate({ to: '/realm/$realmKey/modes', params: { realmKey }, search: { mode: nextSlug, ...(picked.length > 0 ? { path: picked.map(itemKey).join('|') } : {}) }, replace: true });
  const pick = (index: number, item: ModeItem) => void go(slug, [...path.slice(0, index), item]);
  const switchTo = (next: KnowledgeMode) => {
    const nextSlug = slugOf(next);
    const back = remembered.get(nextSlug) ?? '';
    void navigate({ to: '/realm/$realmKey/modes', params: { realmKey }, search: { mode: nextSlug, ...(back ? { path: back } : {}) }, replace: true });
  };

  /** Arrows move within and between columns; Enter opens the item in its own page. */
  function onKey(event: KeyboardEvent<HTMLButtonElement>, column: number, row: number, item: ModeItem) {
    const target = (c: number, r: number) => columnsRef.current?.querySelector<HTMLButtonElement>(`[data-cell="${c}:${r}"]`);
    const move = (c: number, r: number) => {
      const next = target(c, r);
      if (!next) return;
      event.preventDefault();
      next.focus();
    };
    if (event.key === 'ArrowDown') move(column, row + 1);
    else if (event.key === 'ArrowUp') move(column, row - 1);
    else if (event.key === 'ArrowRight') {
      if (path[column] && itemKey(path[column]) === itemKey(item)) move(column + 1, 0);
      else {
        event.preventDefault();
        pick(column, item);
      }
    } else if (event.key === 'ArrowLeft') move(column - 1, Math.max(0, columns[column - 1]?.items.findIndex((i) => path[column - 1] && itemKey(i) === itemKey(path[column - 1]!)) ?? 0));
    else if (event.key === 'Enter') {
      const to = openTarget(world, realmKey, item);
      if (to) {
        event.preventDefault();
        void navigate(to);
      }
    }
  }

  const info = KNOWLEDGE_MODES[mode];
  return (
    <article className="document knowledge-modes">
      <p className="eyebrow">Knowledge Modes</p>
      <h1>Knowledge Modes</h1>
      <div className="actions view-switch mode-switcher" role="group" aria-label="Mode">
        {MODES.map((m) => {
          const k = keysOf(MODE_SHORTCUTS[m]).map(keyLabel)[0];
          return (
            <button key={m} type="button" className={m === mode ? 'tab selected' : 'tab'} aria-pressed={m === mode} title={`${KNOWLEDGE_MODES[m].tagline}${k ? `\n\nShortcut: ${k}` : ''}`} onClick={() => switchTo(m)}>
              {KNOWLEDGE_MODES[m].glyph} {KNOWLEDGE_MODES[m].label}
            </button>
          );
        })}
        <button type="button" className="quiet" title="Read the realm again (after editing elsewhere)" onClick={() => void router.invalidate()}>
          ↻
        </button>
      </div>
      <p className="lead">{info.tagline}</p>
      <p className="mode-breadcrumb meta" aria-live="polite">
        {path.length === 0 ? 'Pick something in the first column to begin.' : path.map((p) => p.title).join('  ›  ')}
      </p>
      <div className="mode-layout">
        <div className="mode-columns" ref={columnsRef}>
          {columns.map((column, c) => (
            <section key={`${c}-${column.title}`} className="mode-column" aria-label={column.title}>
              <header>
                <span className="mode-column-title">{column.title.toUpperCase()}</span>
                <span className="meta">{column.items.length}</span>
              </header>
              {column.items.length === 0 ? (
                <p className="meta">{column.emptyText.trim() === '' ? 'Nothing here.' : column.emptyText}</p>
              ) : (
                <ul role="listbox" aria-label={column.title}>
                  {column.items.map((item, r) => {
                    const selected = path[c] !== undefined && itemKey(path[c]!) === itemKey(item);
                    return (
                      <li key={itemKey(item)} role="option" aria-selected={selected}>
                        <button
                          type="button"
                          data-cell={`${c}:${r}`}
                          className={['mode-item', selected ? 'selected' : '', item.secret ? 'secret' : ''].filter(Boolean).join(' ')}
                          title={item.subtitle ? `${item.title}\n${item.subtitle}` : item.title}
                          onClick={() => pick(c, item)}
                          onDoubleClick={() => {
                            const to = openTarget(world, realmKey, item);
                            if (to) void navigate(to);
                          }}
                          onKeyDown={(e) => onKey(e, c, r, item)}
                        >
                          <span className="mode-glyph" aria-hidden="true">
                            {item.glyph}
                          </span>
                          <span className="mode-text">
                            <span className="mode-item-title">{item.title}</span>
                            {item.subtitle && <span className="meta">{item.subtitle}</span>}
                          </span>
                          {item.secret && (
                            <span title="GM only" aria-label="GM only">
                              🙈
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ))}
        </div>
        <aside className="mode-preview" aria-label="Preview">
          <Preview navigator={navigator} world={world} realmKey={realmKey} mode={mode} item={lastPick} />
        </aside>
      </div>
    </article>
  );
}

function Preview({ navigator, world, realmKey, mode, item }: { navigator: ModeNavigator; world: ModeWorldData; realmKey: string; mode: KnowledgeMode; item: ModeItem | null }) {
  const info = KNOWLEDGE_MODES[mode];
  if (!item) {
    return (
      <>
        <h2>
          {info.glyph} {info.label} mode
        </h2>
        <p className="meta">{info.columns.join('  →  ')}</p>
        <p>Pick something in a column to see what's connected to it in the next one. Use the arrow keys to move within and between columns, and double-click (or press Enter) to open something in its own page.</p>
      </>
    );
  }
  const detail = navigator.describe(item);
  const to = openTarget(world, realmKey, item);
  return (
    <>
      <p className="eyebrow">{detail.kind.toUpperCase()}</p>
      <h2>
        {item.glyph} {detail.heading}
      </h2>
      {detail.secret && <p className="chip secret-badge">🙈 GM only - players shouldn't see this</p>}
      {to && (
        <Link className="button primary" {...to}>
          {openLabel(item)} →
        </Link>
      )}
      {detail.body && <p className="pre-line">{detail.body}</p>}
      {detail.facts.length > 0 && (
        <>
          <h3>Facts</h3>
          <ul className="mode-lines">
            {detail.facts.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </>
      )}
      {detail.related.length > 0 && (
        <>
          <h3>Connections</h3>
          <ul className="mode-lines">
            {detail.related.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function openLabel(item: ModeItem): string {
  switch (item.kind) {
    case 'STORY':
    case 'SCENE':
      return 'Open Story Studio';
    case 'CAMPAIGN':
    case 'SESSION':
    case 'ENCOUNTER':
    case 'COMBATANT':
      return 'Open Game Master Toolkit';
    case 'FINDING':
      return 'Open Consistency Check';
    case 'MAP':
    case 'PIN':
    case 'REGION':
      return 'Open in Atlas';
    default:
      return 'Open Entry';
  }
}

/** Where an item opens: its entry, its story or scene, its campaign's tab, the Consistency Check. Null for none (maps wait for the Atlas). */
function openTarget(world: ModeWorldData, realmKey: string, item: ModeItem): LinkProps | null {
  const entry = (entryId: string): LinkProps => ({ to: '/realm/$realmKey/entry/$entryId', params: { realmKey, entryId }, search: {} });
  const campaignOf = (find: (c: ModeWorldData['campaigns'][number]) => boolean) => world.campaigns.find(find)?.id;
  switch (item.kind) {
    case 'ENTRY':
      return entry(item.refId);
    case 'STORY':
      return { to: '/realm/$realmKey/story/$projectId', params: { realmKey, projectId: item.refId }, search: {} };
    case 'SCENE': {
      const story = world.stories.find((s) => s.scenes.some((x) => x.id === item.refId));
      return story ? { to: '/realm/$realmKey/story/$projectId', params: { realmKey, projectId: story.id }, search: { item: item.refId } } : null;
    }
    case 'CAMPAIGN':
      return { to: '/realm/$realmKey/campaigns/$campaignId', params: { realmKey, campaignId: item.refId }, search: {} };
    case 'SESSION':
    case 'ENCOUNTER':
    case 'COMBATANT': {
      const id =
        item.kind === 'SESSION'
          ? campaignOf((c) => c.sessions.some((s) => s.id === item.refId) || (item.refId === '' && c.encounters.some((e) => e.sessionId === null)))
          : item.kind === 'ENCOUNTER'
            ? campaignOf((c) => c.encounters.some((e) => e.id === item.refId))
            : campaignOf((c) => c.encounters.some((e) => e.combatants.some((x) => x.id === item.refId)));
      if (!id) return null;
      return { to: '/realm/$realmKey/campaigns/$campaignId', params: { realmKey, campaignId: id }, search: item.kind === 'SESSION' ? { tab: 'sessions' } : { tab: 'encounters' } };
    }
    case 'FINDING':
      return { to: '/realm/$realmKey/consistency', params: { realmKey } };
    case 'MAP':
      return { to: '/realm/$realmKey/atlas/$mapId', params: { realmKey, mapId: item.refId }, search: {} };
    case 'PIN':
    case 'REGION': {
      const on = world.maps.find((m) => (item.kind === 'PIN' ? m.markers : m.regions).some((p) => p.id === item.refId));
      if (!on) return null;
      return { to: '/realm/$realmKey/atlas/$mapId', params: { realmKey, mapId: on.id }, search: item.kind === 'PIN' ? { pin: item.refId } : { region: item.refId } };
    }
    case 'EVENT':
    case 'QUESTION':
      return item.entryId !== null ? entry(item.entryId) : null;
    default:
      return null;
  }
}
